import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import BookCard from './BookCard';
import { UI_TEXT } from '../utils/constants';

const BOOKS_PER_PAGE = 12;

const BookList = ({ books, searchQuery, selectedGenre, selectedAge, showOnlyNew, isSaved, onToggleSave }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const filteredBooks = books.filter(book => {
    const matchesSearch = searchQuery === '' ||
      book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGenre = selectedGenre === 'Усі' || book.genre === selectedGenre;
    const matchesAge = selectedAge === 'Усі' || book.ageCategory === selectedAge;
    const matchesNew = !showOnlyNew || book.isNew === true;
    return matchesSearch && matchesGenre && matchesAge && matchesNew;
  });

  const totalPages = Math.max(1, Math.ceil(filteredBooks.length / BOOKS_PER_PAGE));

  // Reset to page 1 whenever filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedGenre, selectedAge]);

  // Clamp currentPage if totalPages shrinks (e.g. fewer results after filter change)
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const pageStart = (currentPage - 1) * BOOKS_PER_PAGE;
  const pageEnd = pageStart + BOOKS_PER_PAGE;
  const booksOnPage = filteredBooks.slice(pageStart, pageEnd);

  const isFirst = currentPage === 1;
  const isLast = currentPage === totalPages;

  const buttonBase =
    'inline-flex items-center justify-center w-10 h-10 rounded-xl border border-outline-variant/40 bg-white text-primary transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-1 hover:enabled:bg-primary-fixed/40 hover:enabled:border-outline-variant';
  const buttonDisabled = 'opacity-40 cursor-not-allowed';

  return (
    <div className="mt-8">
      {/* Results Count */}
      <div
        className="mb-4 text-sm text-gray-600"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {filteredBooks.length} {UI_TEXT.booksFound}
      </div>

      {/* Books Grid or Empty State */}
      {filteredBooks.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">{UI_TEXT.notFound}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {booksOnPage.map(book => (
              <BookCard
                key={book.id}
                book={book}
                isSaved={isSaved ? isSaved(book.id) : false}
                onToggleSave={onToggleSave}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav
              className="mt-10 flex items-center justify-center gap-4"
              role="navigation"
              aria-label={UI_TEXT.paginationNav}
            >
              <button
                className={`${buttonBase} ${isFirst ? buttonDisabled : ''}`}
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={isFirst}
                aria-disabled={isFirst}
                aria-label={UI_TEXT.prevPage}
              >
                <ChevronLeft size={20} />
              </button>

              <span
                className="px-4 py-1.5 rounded-full bg-primary-container text-on-primary-container text-sm font-semibold tracking-wide select-none"
                aria-live="polite"
                aria-atomic="true"
              >
                {currentPage} / {totalPages}
              </span>

              <button
                className={`${buttonBase} ${isLast ? buttonDisabled : ''}`}
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={isLast}
                aria-disabled={isLast}
                aria-label={UI_TEXT.nextPage}
              >
                <ChevronRight size={20} />
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default BookList;
